/**
 * Діалог створення/редагування власної ролі (Порція Б.5; ч.1311 — переверстано «для людини»).
 *
 * Створення (role=null) — два кроки: основа (одна з канонічних системних ролей) → назва і
 * права. Редагування (role=AccessRole) — лише другий крок (основу не змінити).
 *
 * ч.1311 (рішення власника: «права разбросаны как попало — сгруппировать понятно + кнопки
 * внизу статично»): права показуються групами за ОБЛАСТЮ обліку з довідника прав (продажі,
 * закупівлі, гроші, податки, кадри…; `permissionGroups.ts` ← сервер `permission_areas.py`),
 * усередині — від перегляду до видалення; у кожній групі «усі / жодного», зверху пошук і
 * «лише обрані»; кнопки дій — на статичній нижній планці (`drawer__actions`), прокручується
 * лише перелік прав.
 *
 * Правила UI (віддзеркалює backend):
 *  - FORBIDDEN_IN_CUSTOM_ROLES у формі не показуються (backend віддасть 422).
 *  - Права, які сервер не перевіряє (`kind` у довіднику, ч.1089/ч.1108), — сірим з підписом.
 *  - Основа — лише з CANONICAL_SYSTEM_ROLE_CODES; власні ролі основою бути не можуть.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  accessRoles,
  CANONICAL_SYSTEM_ROLE_CODES,
  FORBIDDEN_IN_CUSTOM_ROLES,
  type AccessRole,
  type RecordScope,
} from '@/api/admin';
import { humanizeBackendError } from '@/utils/backendError';
import { groupPermissions } from './permissionGroups';
import {
  kindsOfPermission, PermScopeRow, relevantKinds, scopeRowOwners, scopesToSave, withDefaultOwn,
} from './RecordScopesBlock';

interface Props {
  role: AccessRole | null;          // null = створення; AccessRole = редагування
  systemRoles: AccessRole[];          // системні ролі з RolesSection (вже відсортовані)
  onClose: () => void;
  onSaved: () => void;
}

export default function RoleEditorDialog({ role, systemRoles, onClose, onSaved }: Props) {
  const isEdit = role !== null;
  const [step, setStep] = useState<1 | 2>(isEdit ? 2 : 1);
  const [sourceCode, setSourceCode] = useState<string>(
    isEdit ? (role!.created_from_role_code ?? 'ACCOUNTANT') : '',
  );
  const [name, setName] = useState<string>(isEdit ? role!.name : '');
  const [permissions, setPermissions] = useState<Set<string>>(
    isEdit ? new Set(role!.permissions) : new Set(),
  );
  // ч.1312: межі записів за видами (немає ключа — усі записи).
  const [recordScopes, setRecordScopes] = useState<Record<string, RecordScope>>(
    isEdit ? { ...(role!.record_scopes ?? {}) } : {},
  );
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const availableSources = useMemo(
    () => systemRoles.filter((r) => CANONICAL_SYSTEM_ROLE_CODES.includes(r.code)),
    [systemRoles],
  );

  // При зміні основи — галочки з системної ролі (без заборонених).
  useEffect(() => {
    if (!isEdit && sourceCode) {
      const src = systemRoles.find((r) => r.code === sourceCode);
      if (src) setPermissions(new Set(src.permissions.filter((p) => !FORBIDDEN_IN_CUSTOM_ROLES.includes(p))));
      // ч.1317: права основи — «всі» (як у готовій ролі); «тільки власні» — лише на нові галки.
      setRecordScopes({});
    }
  }, [sourceCode, systemRoles, isEdit]);

  // ч.1119: основа «лише перегляд» — власна роль теж лише для перегляду.
  const readOnlyRole = isEdit
    ? role!.is_read_only === true
    : systemRoles.find((r) => r.code === sourceCode)?.is_read_only === true;

  // Усі відомі права — обʼєднання системних ролей (без окремого ендпоінта).
  const { data: allPerms } = useQuery({
    queryKey: ['all-permissions'],
    queryFn: async () => {
      const all = new Set<string>();
      for (const r of systemRoles) for (const p of r.permissions) all.add(p);
      return Array.from(all).sort();
    },
    enabled: systemRoles.length > 0,
  });
  const { data: permCatalog } = useQuery({
    queryKey: ['permissions-catalog'],
    queryFn: () => accessRoles.permissionsCatalog(),
    staleTime: Infinity,
  });

  const { data: scopeKinds } = useQuery({
    queryKey: ['record-scope-kinds'],
    queryFn: () => accessRoles.recordScopeKinds(),
    staleTime: Infinity,
  });
  const roleKinds = useMemo(() => relevantKinds(scopeKinds, permissions), [scopeKinds, permissions]);

  const displayablePerms = useMemo(
    () => (allPerms ?? []).filter((p) => !FORBIDDEN_IN_CUSTOM_ROLES.includes(p)),
    [allPerms],
  );
  const groups = useMemo(() => groupPermissions(displayablePerms, permCatalog), [displayablePerms, permCatalog]);
  const q = search.trim().toLowerCase();
  const visibleGroups = useMemo(() => groups
    .map((g) => ({
      ...g,
      perms: g.perms.filter((p) => (!q || p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q))
        && (!onlySelected || permissions.has(p.code))),
    }))
    .filter((g) => g.perms.length > 0), [groups, q, onlySelected, permissions]);
  const selectedCount = displayablePerms.filter((p) => permissions.has(p)).length;
  // ч.1317: перемикач виду — під першим відміченим правом виду (у порядку показу).
  const scopeOwners = useMemo(() => scopeRowOwners(
    scopeKinds, visibleGroups.flatMap((g) => g.perms.map((p) => p.code)).filter((c) => permissions.has(c)),
  ), [scopeKinds, visibleGroups, permissions]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const record_scopes = scopesToSave(recordScopes, roleKinds);
      if (isEdit) return accessRoles.patch(role!.id, { name, permissions: Array.from(permissions), record_scopes });
      return accessRoles.create({
        name, source_system_role_code: sourceCode, permissions: Array.from(permissions), record_scopes,
      });
    },
    onSuccess: onSaved,
    onError: (e) => setError(humanizeBackendError(e)),
  });

  // ч.1317: відмітили галку права, вид якого ще не був у ролі, — «тільки власні» за замовчуванням.
  function applyPerms(next: Set<string>) {
    setRecordScopes((prev) => withDefaultOwn(prev, permissions, next, scopeKinds));
    setPermissions(next);
  }
  function togglePerm(code: string) {
    const next = new Set(permissions);
    if (next.has(code)) next.delete(code); else next.add(code);
    applyPerms(next);
  }
  function setGroup(codes: string[], on: boolean) {
    const next = new Set(permissions);
    for (const c of codes) { if (on) next.add(c); else next.delete(c); }
    applyPerms(next);
  }
  function setKindScope(kind: string, v: RecordScope | 'ALL') {
    setRecordScopes((prev) => {
      const next = { ...prev };
      if (v === 'ALL') delete next[kind]; else next[kind] = v;
      return next;
    });
  }
  const canSubmit = !!name.trim() && (isEdit || !!sourceCode);

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
        display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 50 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="role-editor"
        style={{ background: 'var(--bg-panel)', color: 'var(--text)', borderRadius: 'var(--radius)',
          width: 900, maxWidth: '94vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem 1.5rem 0.5rem' }}>
          <h3 style={{ margin: 0, flex: 1 }}>
            {isEdit ? `Редагування ролі «${role!.name}»` : 'Створити власну роль'}
          </h3>
          <button type="button" onClick={onClose} aria-label="Закрити">✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 1.5rem 0.5rem' }}>
          {/* Крок 1: основа (лише при створенні) */}
          {!isEdit && step === 1 && (
            <>
              <div style={{ marginBottom: '0.75rem', color: 'var(--text-secondary)' }}>
                Оберіть готову роль як основу — її права підставляться галочками, далі їх можна змінити.
              </div>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {availableSources.map((r) => (
                  <label key={r.id} style={{
                    display: 'flex', padding: '0.6rem 0.75rem', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', cursor: 'pointer',
                    background: sourceCode === r.code ? 'var(--primary-soft)' : 'var(--bg-panel)',
                  }}>
                    <input type="radio" name="source" value={r.code} checked={sourceCode === r.code}
                      onChange={() => setSourceCode(r.code)} style={{ marginRight: '0.75rem', width: 'auto' }} />
                    <div>
                      <div style={{ fontWeight: 600 }}>{r.name}</div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        {r.permissions.length} прав{r.description ? ` · ${r.description}` : ''}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}

          {/* Крок 2: назва + права */}
          {step === 2 && (
            <>
              <div className="field" style={{ marginBottom: '0.75rem' }}>
                <label>Назва ролі</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Наприклад: Помічник бухгалтера" />
                {!isEdit && (
                  <small style={{ color: 'var(--text-secondary)' }}>
                    На основі: <strong>{systemRoles.find((r) => r.code === sourceCode)?.name ?? sourceCode}</strong>
                  </small>
                )}
              </div>
              {readOnlyRole && (
                <div className="mc__panel mc__panel--info" data-testid="role-editor-read-only-note"
                  style={{ marginBottom: '0.75rem', fontSize: '0.875rem' }}>
                  Це роль лише для перегляду. Користувач бачитиме те, що відмічено нижче, але
                  створювати, змінювати чи проводити не зможе — права на зміну в такій ролі не
                  діють. Потрібна роль, яка змінює дані, — створіть її на іншій основі,
                  наприклад «Бухгалтер».
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                <strong>Права (обрано {selectedCount} з {displayablePerms.length})</strong>
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="пошук права…" aria-label="Пошук права"
                  style={{ marginLeft: 'auto', width: 240 }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={onlySelected} onChange={(e) => setOnlySelected(e.target.checked)}
                    style={{ width: 'auto' }} />
                  лише обрані
                </label>
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
                Права згруповано за розділами обліку; у кожному — від перегляду до видалення.
                Адміністративні права, закриття періоду і ручні операції у власних ролях
                недоступні — їх тут немає.
                {' '}<span style={{ color: 'var(--muted)' }}>Сірі</span> — сервер їх не перевіряє: підпис поруч
                пояснює, чи це системне право (керує платформа), чи воно діє лише в інтерфейсі, чи поки не діє.
                {' '}Під правом на документи чи контрагентів — вибір <b>«тільки власні»</b> (записи, за які людина
                відповідальна: створені нею або передані їй) чи <b>«всі»</b>; де документи будуються на підставі
                інших — ще «власні і на їх підставі». Власник і адміністратор акаунта бачать усе.
              </div>

              {visibleGroups.length === 0 && (
                <div style={{ color: 'var(--muted)', padding: '0.5rem 0' }}>
                  {q || onlySelected ? 'Нічого не знайдено.' : 'Завантаження…'}
                </div>
              )}
              {visibleGroups.map((g) => {
                const codes = g.perms.map((p) => p.code);
                const chosen = codes.filter((c) => permissions.has(c)).length;
                return (
                  <section key={g.key} data-testid={`perm-area-${g.key}`}
                    style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: '0.6rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0.7rem',
                      background: 'var(--bg-soft)', borderBottom: '1px solid var(--border)' }}>
                      <strong style={{ flex: 1 }}>{g.label}</strong>
                      <span style={{ color: 'var(--muted)', fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>
                        {chosen} з {codes.length}
                      </span>
                      <button type="button" className="btn-link" onClick={() => setGroup(codes, true)}
                        disabled={chosen === codes.length}>усі</button>
                      <button type="button" className="btn-link" onClick={() => setGroup(codes, false)}
                        disabled={chosen === 0}>жодного</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                      gap: '0.1rem 1rem', padding: '0.45rem 0.7rem' }}>
                      {g.perms.map((p) => {
                        const isCosmetic = !!p.note;
                        // ч.1317: перемикачі «тільки власні / всі» видів, для яких це право — перше відмічене.
                        const rowKinds = kindsOfPermission(scopeKinds, p.code)
                          .filter((k) => scopeOwners.get(k.code) === p.code);
                        return (
                          <div key={p.code}>
                            <label title={p.code}
                              style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.2rem 0',
                                cursor: 'pointer', opacity: isCosmetic ? 0.55 : 1,
                                color: isCosmetic ? 'var(--muted)' : 'inherit' }}>
                              <input type="checkbox" checked={permissions.has(p.code)} onChange={() => togglePerm(p.code)}
                                style={{ width: 'auto', marginTop: 3 }} />
                              <span>
                                <span style={{ fontWeight: 500 }}>{p.name}</span>
                                {isCosmetic && (
                                  <span style={{ fontSize: '0.75rem', marginLeft: '0.4rem', fontStyle: 'italic' }}>({p.note})</span>
                                )}
                              </span>
                            </label>
                            {rowKinds.map((k) => (
                              <PermScopeRow key={k.code} kind={k} rowKey={p.code}
                                value={recordScopes[k.code] ?? 'ALL'}
                                showLabel={kindsOfPermission(scopeKinds, p.code).length > 1}
                                onChange={(v) => setKindScope(k.code, v)} />
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </>
          )}
        </div>

        {error && (
          <div style={{ margin: '0 1.5rem', background: 'var(--badge-red-bg)', color: 'var(--danger)',
            padding: '0.5rem 0.75rem', borderRadius: 'var(--radius)', whiteSpace: 'pre-wrap' }}>
            {error}
          </div>
        )}
        {/* ч.1311: дії — статично внизу, як у решті вікон проєкту (прокручується лише перелік). */}
        <div className="drawer__actions" style={{ margin: '0 1.5rem 1rem', justifyContent: 'space-between' }}>
          <div>
            {step === 2 && !isEdit && (
              <button type="button" onClick={() => setStep(1)}>назад</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={onClose}>скасувати</button>
            {step === 1 ? (
              <button type="button" className="primary" disabled={!sourceCode} onClick={() => setStep(2)}>далі</button>
            ) : (
              <button type="button" className="primary" disabled={!canSubmit || saveMut.isPending}
                onClick={() => { setError(null); saveMut.mutate(); }}>
                {saveMut.isPending ? 'зберігаємо…' : isEdit ? 'зберегти' : 'створити'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
